import { printMoney } from '@process-creative/slate-theme-tools';

import { Product } from '../../tools/product/product';
import { t } from '../../tools/utilities/string-tools';

const STR_ADD = t('products.product.add_to_cart');
const STR_SOLD_OUT = t('products.product.sold_out');
const STR_FREE = t('products.product.free');
const STR_VIEW = t('products.product.view_product');

/**
 *
 * @param product - Product JSON
 * @returns string of product thumbnail html
 */
export const productThumbnailPrint = (product: Product, clazz?: string, usesNewCollectionItems?: Boolean) => {
  const variant =
    product.selected_or_first_available_variant || product.variants[0];

  // Pricing
  const pricingCreate = () => {
    if (!variant) return '';

    // these are equivalent to the params passed to object.product-pricing.liquid
    const paramClass = 'o-product-thumbnail-secondary__pricing';
    const paramPrice = variant.price;
    const paramComparePrice = variant.compare_at_price;

    // this part is equivalent to object.product-pricing.liquid
    let paramComparePriceMoney = ``;
    let comparePriceHtml = ``;
    let hasCompare = false;
    if (paramComparePrice) {
      paramComparePriceMoney = printMoney(paramComparePrice);

      const compareClasses = `o-product-pricing__money o-product-pricing__compare ${paramClass}-money ${paramClass}-compare`;

      comparePriceHtml = `
        <span
          class="${compareClasses}"
          data-currency
          data-money="${paramComparePrice}"
          data-product-compare-money
        >
          ${paramComparePriceMoney}
        </span>
      `;

      if (paramComparePrice && paramComparePrice > paramPrice) {
        hasCompare = true;
      }
    }

    let hasCompareResult = '';
    if (hasCompare) {
      hasCompareResult = 'has-compare';
    }

    let priceResult = ``;
    if (paramPrice === 0) {
      priceResult = `
        <span
          class="o-product-pricing__money o-product-pricing__price ${paramClass}-money o-subtitle ${paramClass}-price"
          data-currency
          data-product-money
        >
          ${STR_FREE}
        </span>
      `;
    } else {
      priceResult = `
        <span
          class="o-product-pricing__money o-product-pricing__price o-subtitle ${paramClass}-money
          ${paramClass}-price ${hasCompareResult}"
          data-currency
          data-money="${paramPrice}"
          data-product-money
        >
          ${printMoney(paramPrice)}
        </span>
      `;
    }

    const objectProductPricing = `
      <p
        class="o-product-pricing o-paragraph-2 ${paramClass} ${hasCompareResult}"
        data-product-pricing
      >
        ${comparePriceHtml}
        ${priceResult}
      </p>
    `;

    return `
      <a href="${product.url}" class="o-product-thumbnail-secondary__pricing-container">
        ${objectProductPricing}
      </a>
    `;
  };

  // Pricing (new collection item)
  const newCollectionItemPricingCreate = () => {
    if (!variant) return '';

    const paramPrice = variant.price;
    const paramComparePrice = variant.compare_at_price;

    let paramComparePriceMoney = ``;
    let comparePriceHtml = ``;
    if (paramComparePrice) {
      paramComparePriceMoney = printMoney(paramComparePrice);

      comparePriceHtml = `
        <s>${paramComparePriceMoney}</s>
      `;
    }
  
    let priceResult = ``;
    if (paramPrice === 0) {
      priceResult = `
        ${STR_FREE}
      `;
    } else {
      priceResult = `
        ${printMoney(paramPrice)}
      `;
    }

    return `
      <p class="collection-item__price">
        ${priceResult}
        ${comparePriceHtml}
      </p>
    `;
  };

  // Marquee
  const marqueeCreate = () => {
    if (!product.marquee) return '';
    let marqueeArray = product.marquee.split(',');
    const onSale = product.on_sale;
    if (onSale) marqueeArray = t('products.product.on_sale').split(',');

    const duplicate1 = marqueeArray.slice();
    const duplicate2 = marqueeArray.slice();

    // Make array longer by duplicating and adding
    const duplicatedMarqueeArray = [
      ...marqueeArray,
      ...duplicate1,
      ...duplicate2,
    ];

    let html = `
    <div class="o-product-thumbnail-secondary__marquee test">
     <div class="o-product-thumbnail-secondary__marquee-track">`;

    duplicatedMarqueeArray.forEach((item) => {
      html += `
        <div class="o-product-thumbnail-secondary__marquee-item o-subheading">
          ${item}
        </div>
        `;
    });

    html += `
     </div>
   </div>`;

    return html;
  };

  // Image
  const imageCreate = () => {
    const imageSrc =
      product.thumbnail_image || product.featured_image || product.image || '';
    const hoverImageSrc =
      product.override_hover_image || product.default_hover_image;

    return `
      <a href="${product.url}" class="o-product-thumbnail-secondary__image-container o-responsive-image-container">
        <picture>
          <img src="${imageSrc}" class="o-product-thumbnail-secondary__image o-responsive-image" alt=""/>
        </picture>
      </a>
      <div class="o-product-thumbnail-secondary__hover-image-wrapper">
        <div class="o-product-thumbnail-secondary__hover-image-container o-responsive-image-container">
          <picture>
            <img src="${hoverImageSrc}" class="o-product-thumbnail-secondary__hover-image o-responsive-image" alt=""/>
          </picture>
        </div>
      </div>
    `;
  };

  // Image (new collection item)
  const newCollectionItemImageCreate = () => {
    const imageSrc =
      product.thumbnail_image || product.featured_image || product.image || '';
    const hoverImageSrc =
      product.override_hover_image || product.default_hover_image;

    return `
      <div class="collection-item__image-wrapper">
        <picture>
          <img src="${imageSrc}" class="collection-item__image collection-item__image--main" alt=""/>
        </picture>
        <picture>
          <img src="${hoverImageSrc}" class="collection-item__image collection-item__image--hover" alt=""/>
        </picture>
      </div>
    `;
  };

  const atcViewLabelCreate = () => {
    let titleArray: string[];
    let title: string;
    if (product.title.includes('(')) {
      titleArray = product.title.split('(');
      title = titleArray[0];
    } else {
      titleArray = product.title.split('-');
      title = titleArray[0];
    }
    const label = STR_VIEW.replace('{{productName}}', title);
    return `${label}`;
  };

  // ATC button
  const atcButtonCreate = (clazz: string) => {
    let titleArray: string[];
    let title: string;
    if (product.title.includes('(')) {
      titleArray = product.title.split('(');
      title = titleArray[0];
    } else {
      titleArray = product.title.split('-');
      title = titleArray[0];
    }

    // If product has only 1 product we'll show the atc button
    if (product.has_only_default_variant || product.variants.length <= 1) {
      return `
        <div class="o-product-thumbnail-secondary__button-wrapper ${clazz}">
          <button
            type="submit"
            title="${title}"
            class="o-product-thumbnail-secondary__button o-btn has-js is-full-width
            ${variant.available ? '' : 'is-disabled'}"
            data-product-thumbnail-add-btn
            ${variant.available ? '' : 'disabled="disabled"'}
          >

            <span
              class="o-product-thumbnail-secondary__button-label o-btn__label"
              data-product-thumbnail-add-btn-label
            >
              ${variant.available ? STR_ADD : STR_SOLD_OUT}
            </span>
          </button>
        </div>
      `;
    }

    // If product has more then 1 variant we'll just link to the PDP
    return `
      <div class="o-product-thumbnail-secondary__button-wrapper ${clazz}">
        <a
          href="${product.url}"
          title="${title}"
          class="o-product-thumbnail-secondary__button o-btn is-full-width"
        >
          <span
            class="o-product-thumbnail-secondary__button-label o-btn__label"
          >
            ${product.available ? atcViewLabelCreate() : STR_SOLD_OUT}
          </span>
        </a>
      </div>
    `;
  };

  const subtitleCreate = () => {
    let titleArray;
    let subtitle;
    if (product.title.includes('(')) {
      titleArray = product.title.split('(');
      subtitle = titleArray[1].replace(')', '');
    } else {
      titleArray = product.title.split('-');
      subtitle = titleArray[1];
    }
    if (subtitle) {
      return `
       <a href="${product.url}"class="o-product-thumbnail-secondary__subtitle-container">  
          <p class="o-product-thumbnail-secondary__subtitle">
            ${subtitle}
          </p>
        </a>`;
    } else {
      return '';
    }
  };

  // Title
  const titleCreate = () => {
    let titleArray;
    let title;
    if (product.title.includes('(')) {
      titleArray = product.title.split('(');
      title = titleArray[0];
    } else {
      titleArray = product.title.split('-');
      title = titleArray[0];
    }
    return title;
  };

  // Title (new collection item)
  const newCollectionItemTitleCreate = () => {
    let titleArray;
    if (product.title.includes('(')) {
      titleArray = product.title.replace(')', '').split('(');
    } else {
      titleArray = product.title.split('-');
    }
    const titleToRender = titleArray[0];
    const subtitleToRender = titleArray[1] || '';
    return `
      <p class="collection-item__title">
        ${titleToRender}
      </p>
      <p class="collection-item__subtitle">
        ${subtitleToRender}
      </p>
    `;
  };

  // Rating (new collection item)
  const newCollectionItemRatingCreate = () => {
    return `
      <div class="collection-item__rating">
        <span class="stamped-product-reviews-badge stamped-main-badge" data-id="${product.id}"
          data-product-sku="${product.handle}" data-product-title="${product.title}"
          aria-hidden="false" style="display: block;">
        </span>
      </div>
      <script>StampedFn.reloadUGC();</script>
    `;
  };

  if (usesNewCollectionItems) {
    return `
      <div 
        class="collection-item ${clazz}__item" 
        data-product-thumbnail
        data-variant-id="${variant.id}"
      >
        <script type="application/json" data-product-json>
          ${JSON.stringify(product)}
        </script>

        <a href="${product.url}" class="collection-item__wrapper">
          ${newCollectionItemImageCreate()}
          <div class="collection-item__content">
            ${newCollectionItemRatingCreate()}
            ${newCollectionItemTitleCreate()}
            ${newCollectionItemPricingCreate()}
          </div>
        </a>
      </div>
    `;
  } else {
    return `
      <div
        class="o-product-thumbnail-secondary ${clazz}__thumbnail"
        data-product-thumbnail
        data-variant-id="${variant.id}"
      >
        <script type="application/json" data-product-json>
          ${JSON.stringify(product)}
        </script>

        ${marqueeCreate()}

        <div class="o-product-thumbnail-secondary__inner">

          <div class="o-product-thumbnail-secondary__image-wrapper ${clazz}__image-wrapper">
            ${imageCreate()}

          </div>

          <div class="o-product-thumbnail-secondary__content">
            <a href="${product.url}
              class="o-product-thumbnail-secondary__title-container">
              <h3 class="o-product-thumbnail-secondary__title ${clazz}__title o-heading--6">
                ${titleCreate()}
              </h3>
            </a>

            ${subtitleCreate()}

            ${pricingCreate()}
          </div>
        </div>

        ${atcButtonCreate('is-mobile')}
      </div>
    `;
  }
};
